import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardHeader, CardContent, Link, Avatar, FormControlLabel, Switch, IconButton } from '@mui/material';
import { Edit, Delete, RotateLeft } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import CreateUserDialog from './CreateUserDialog';
import { useHasPermission } from '../utils/permissionUtils';
import { getAvatarUrl } from '../utils/avatarUtils';
import { useIsLocked } from '../utils/lockUtils';
import { useDispatch, useSelector } from 'react-redux';
import { deleteUser, restoreUser } from '../redux/slices/users';

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
}))

const DeletedUser = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.action.disabled,
    textDecoration: 'line-through',
    color: theme.palette.text.disabled,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}))

const ActiveUser = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    textDecoration: 'none',
    color: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}))

const UserBox = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
}))

const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
}));


const ClientInfoUserCard = ({ users, clientId, isLoading, isInternal, client }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [clientUsers, setClientUsers] = useState(users);
    const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);
    const [showDisabled, setShowDisabled] = useState(false);
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const canEditClient = hasPermission('editClient');
    const canEditOwnClient = hasPermission('editOwnClient');
    const isLocked = useIsLocked(client, null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const ownUser = useSelector(state => state.auth.currentUser._id);

    const handleOpenCreateUserDialog = () => {
        setOpenCreateUserDialog(true);
    }

    const handleCloseCreateUserDialog = () => {
        setOpenCreateUserDialog(false);
        setIsEditing(false);
        setEditingUser(null);
    }

    const handleUserCreate = (user) => {
        setClientUsers([...clientUsers, user]);
        setOpenCreateUserDialog(false);
    }

    const handleEditUser = (user) => {
        setEditingUser(user);
        setIsEditing(true);
        setOpenCreateUserDialog(true);
    }

    const handleUserUpdate = (user) => {
        setClientUsers(clientUsers.map(u => u._id === user._id ? user : u));
        setIsEditing(false);
    }

    const handleDeleteUser = (user) => {
        dispatch(deleteUser({ userId: user._id, callback: () => {
            setClientUsers(clientUsers.map(u => u._id === user._id ? { ...u, isDeleted: true } : u));
        }}));
    }

    const handleRestoreUser = (user) => {
        dispatch(restoreUser({ userId: user._id, callback: () => {
            setClientUsers(clientUsers.map(u => u._id === user._id ? { ...u, isDeleted: false } : u));
        }}));
    }

    useEffect(() => {
        setClientUsers(users);
    }, [users]);

    return (
        <StyledCard>
            <CardHeader 
              title={`Users (${showDisabled ? clientUsers?.length : clientUsers?.filter(user => user.active).length || 0})`} 
              action={
                <Box display="flex" alignItems="center" gap={1}>
                  {canSeeInternal && <FormControlLabel
                    control={
                      <Switch 
                        size="small"
                        checked={showDisabled}
                        onChange={(e) => setShowDisabled(e.target.checked)}
                      />
                    }
                    label="Show Disabled"
                  />}
                  {(canEditClient || canEditOwnClient) && <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenCreateUserDialog}
                    disabled={isLocked}
                  >
                    Add User
                  </Button>}
                </Box>
              } 
            />
            <CardContent>
                <Box>
                    {clientUsers !== undefined && clientUsers.filter(user => showDisabled ? true : user.active).map((user) => (
                        user.isDeleted ? 
                            <DeletedUser key={user._id}>
                                <UserBox>
                                    <Box display="flex" alignItems="center">
                                        <Avatar src={getAvatarUrl(user)} alt={user.fullName}>{user.first_name.charAt(0)}{user.last_name.charAt(0)}</Avatar>
                                        <Box ml={2}>
                                            <StyledLink onClick={() => navigate(`/user/${user._id}`)} fontWeight="medium">{user.fullName}</StyledLink>
                                            <Typography variant="body2" color="textSecondary">{user.title}</Typography>
                                        </Box>
                                    </Box>
                                    {(canEditClient || canEditOwnClient) && !isLocked && <Box>
                                        <IconButton onClick={() => handleRestoreUser(user)}>
                                            <RotateLeft />
                                        </IconButton>
                                    </Box>}
                                </UserBox>
                            </DeletedUser>
                        :
                            <ActiveUser key={user._id}>
                                <UserBox>
                                    <Box display="flex" alignItems="center">
                                        <Avatar src={getAvatarUrl(user)} alt={user.fullName}>{user.first_name.charAt(0)}{user.last_name.charAt(0)}</Avatar>
                                        <Box ml={2}>
                                            <StyledLink onClick={() => navigate(`/user/${user._id}`)} fontWeight="medium">{user.fullName}</StyledLink>
                                            <Typography variant="body2" color="textSecondary">{user.title}</Typography>
                                        </Box>
                                    </Box>
                                    {(canEditClient || canEditOwnClient) && !isLocked && <Box>
                                        <IconButton onClick={() => handleEditUser(user)}>
                                            <Edit />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteUser(user)} disabled={ownUser === user._id}>
                                            <Delete />
                                        </IconButton>
                                    </Box>}
                                </UserBox>
                            </ActiveUser>
                    ))}
                </Box>
            </CardContent>
            {isLoading ? <></> : (
                <CreateUserDialog open={openCreateUserDialog} onCancel={handleCloseCreateUserDialog} onCreate={handleUserCreate} clientId={clientId} isInternal={isInternal} isEditing={isEditing} onUpdate={handleUserUpdate} editingUser={editingUser} />
            )}
        </StyledCard >
    )
};

export default ClientInfoUserCard;